'use client';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { getBackgroundClasses, getTextClasses, getBorderClasses, getInputClasses, getAccentClasses } from '@/lib/theme-utils';
import { useTheme } from '@/lib/theme-context';

export interface ProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  name: string;
  email: string;
  avatarUrl?: string;
  onAccountSettings?: () => void;
  onLogout?: () => void;
  isLoggingOut?: boolean; // Add loading state prop
}

export function ProfileDialog({
  open,
  onOpenChange,
  name,
  email,
  avatarUrl = "/placeholder.svg?height=64&width=64",
  onAccountSettings,
  onLogout,
  isLoggingOut = false,
}: ProfileDialogProps) {
  const { theme } = useTheme();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>User Profile</DialogTitle>
          <DialogDescription>
            View and manage your account information.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-2 py-4">
          <Avatar className="w-16 h-16">
            <AvatarImage src={avatarUrl} alt="User" />
            <AvatarFallback className="text-2xl bg-surface-dark" style={{ color: 'var(--theme-accent)' }}>
              {name
                .split(' ')
                .map((n) => n[0])
                .join('')
                .toUpperCase()
                .slice(0, 2)}
            </AvatarFallback>
          </Avatar>
          <div className="text-lg font-semibold">{name}</div>
          <div className={`text-sm ${getTextClasses(theme, 'muted')}`}>{email}</div>
        </div>
        <DialogFooter>
          <div className="flex flex-col w-full gap-y-2">
            <Button 
              variant="outline" 
              className="w-full" 
              onClick={onAccountSettings}
              disabled={isLoggingOut}
            >
              Account Settings
            </Button>
            <Button 
              variant="destructive" 
              className="w-full" 
              onClick={onLogout}
              disabled={isLoggingOut || !onLogout}
            >
              {isLoggingOut ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Logging Out...
                </>
              ) : (
                'Log Out'
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}